/**
 * MMG Countdown — frontend ticker (vanilla JS, no React required).
 *
 * The PHP render callback produces the full HTML structure with
 * data-dest-date on the wrapper and class-named digit spans.
 *
 * Each block gets a controller object so the destination date can be
 * changed dynamically after page load. Three ways to do it:
 *
 *   1. Element method:
 *      el.mmgCountdown.setDate( '2026-12-31T00:00:00' );
 *
 *   2. Global API (selector or element):
 *      window.mmgCountdown.setDate( '#my-block', '2026-12-31T00:00:00' );
 *      window.mmgCountdown.setDate( el,          '2026-12-31T00:00:00' );
 *
 *   3. Custom DOM event on the element:
 *      el.dispatchEvent( new CustomEvent( 'mmg-countdown:set-date', {
 *          detail: { date: '2026-12-31T00:00:00' }
 *      } ) );
 */

function mmgCountdownGetRemaining( destDate ) {
	if ( ! destDate ) return 0;
	const diff = ( new Date( destDate ).getTime() - Date.now() ) / 1000;
	return diff > 0 ? diff : 0;
}

function mmgCountdownPad( n ) {
	return String( Math.floor( n ) ).padStart( 2, '0' );
}

/**
 * Initialise a single countdown block element.
 * Returns the controller object that is also attached to `el.mmgCountdown`.
 *
 * @param {HTMLElement} el
 * @returns {{ setDate: Function, stop: Function, start: Function }}
 */
function mmgCountdownInit( el ) {
	let destDate  = el.dataset.destDate;
	let intervalId = null;

	function tick() {
		const s = mmgCountdownGetRemaining( destDate );

		const dEl = el.querySelector( '.mmg-countdown__days' );
		const hEl = el.querySelector( '.mmg-countdown__hours' );
		const mEl = el.querySelector( '.mmg-countdown__minutes' );
		const sEl = el.querySelector( '.mmg-countdown__seconds' );

		// Cascade: when a higher unit's span is absent, roll its value into the next visible one.
		const rawDays       = Math.floor( s / 86400 );
		const rawHoursOfDay = Math.floor( ( s % 86400 ) / 3600 );
		const rawMinsOfHour = Math.floor( ( s % 3600 ) / 60 );
		const rawSecsOfMin  = Math.floor( s % 60 );

		const hours   = dEl ? rawHoursOfDay : 24 * rawDays  + rawHoursOfDay;
		const minutes = hEl ? rawMinsOfHour : 60 * hours    + rawMinsOfHour;
		const seconds = mEl ? rawSecsOfMin  : 60 * minutes  + rawSecsOfMin;

		if ( dEl ) dEl.textContent = mmgCountdownPad( rawDays );
		if ( hEl ) hEl.textContent = mmgCountdownPad( hours );
		if ( mEl ) mEl.textContent = mmgCountdownPad( minutes );
		if ( sEl ) sEl.textContent = mmgCountdownPad( seconds );
	}

	function stop() {
		if ( intervalId !== null ) {
			clearInterval( intervalId );
			intervalId = null;
		}
	}

	function start() {
		stop();
		tick(); // immediate first frame
		intervalId = setInterval( tick, 1000 );
	}

	/**
	 * Change the destination date and restart the ticker.
	 *
	 * @param {string} newDate - Any value accepted by `new Date()`,
	 *                           e.g. '2026-12-31T00:00:00' or a Unix ms timestamp.
	 */
	function setDate( newDate ) {
		destDate             = newDate;
		el.dataset.destDate  = newDate; // keep the DOM attribute in sync
		start();
	}

	// Allow the date to be changed by dispatching a custom event on the element.
	el.addEventListener( 'mmg-countdown:set-date', function ( e ) {
		if ( e.detail && e.detail.date ) {
			setDate( e.detail.date );
		}
	} );

	const controller = { setDate, stop, start };

	// Attach controller to the DOM element for direct access.
	el.mmgCountdown = controller;

	if ( destDate ) {
		start();
	}

	return controller;
}

/* -------------------------------------------------------------------------
 * Global API — convenience wrapper around per-element controllers.
 * ------------------------------------------------------------------------- */
window.mmgCountdown = {
	/**
	 * Change the destination date for one countdown block.
	 *
	 * @param {Element|string} target  - A DOM element or a CSS selector string.
	 * @param {string}         newDate - New destination date string.
	 */
	setDate( target, newDate ) {
		const el = typeof target === 'string' ? document.querySelector( target ) : target;
		if ( el && el.mmgCountdown ) {
			el.mmgCountdown.setDate( newDate );
		}
	},

	/**
	 * Change the destination date for ALL countdown blocks on the page.
	 *
	 * @param {string} newDate - New destination date string.
	 */
	setDateAll( newDate ) {
		document.querySelectorAll( '.wp-block-mmg-countdown' ).forEach( function ( el ) {
			if ( el.mmgCountdown ) {
				el.mmgCountdown.setDate( newDate );
			}
		} );
	},
};

document.addEventListener( 'DOMContentLoaded', function () {
	document.querySelectorAll( '.wp-block-mmg-countdown' ).forEach( mmgCountdownInit );
} );
